import { differenceInSeconds } from 'date-fns'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { CombinedError, useMutation, useQuery } from 'urql'

import { LoggerProvider } from '@/contexts/LoggerContext'
import {
  BillType,
  CartPaymentQuery,
  CoreOrderingOrderStatus,
  CreateBillDocument,
  GetPaymentsDocument,
  GetPaymentsQuery,
  ListBillOrdersDocument,
  NewPaymentFormQuery,
  PaymentV2MetaQuery,
} from '@/gql/graphql'
import { useEffectOnce } from '@/hooks/useEffectOnce'
import { useLoggerLegacy } from '@/hooks/useLogger'
import { useLogin } from '@/hooks/useLogin'

import {
  PaymentMethod,
  PaymentMethodKey,
  usePaymentFormMethods,
  UsePaymentMethodsReturnType,
} from '../hooks/usePaymentFormMethods'
import { usePaymentIntent } from '../hooks/usePaymentIntent'

export type NewPaymentProps = {
  isNew: boolean
  isNewGroupOrder?: boolean
  isNewPaystack: boolean
}
export type GetPaymentsPaymentResponse =
  GetPaymentsQuery['getPayments']['items'][0]

export type PaymentControllerProps = {
  venue: NonNullable<NewPaymentFormQuery['guestVenue']>
  currentUser: NonNullable<ReturnType<typeof useLogin>['currentUser']>
  cart?: CartPaymentQuery['getCart']
  cartFetching?: boolean
  cartStale?: boolean
  totalInCents: number
  isNewGroupOrder?: boolean // if starting a new tab
  defaultPaymentMethod: PaymentMethod
  showEmailFieldWhenDineIn?: boolean
  paymentProcessors: NonNullable<PaymentV2MetaQuery['listPaymentProcessors']>
} & NewPaymentProps

export const PaymentController = ({
  children,
  currentUser,
  totalInCents,
  showEmailFieldWhenDineIn,
  loader,
  paymentProcessors,
  isNew,
  venue,
  isNewGroupOrder,
  cart,
  ...rest
}: Omit<PaymentControllerProps, 'defaultPaymentMethod'> & {
  children: ReactNode
  loader: ReactNode
}) => {
  const { logEventLegacy } = useLoggerLegacy()
  const currency = venue.currency
  const venueId = venue.id
  const [billId, setBillId] = useState<string | null>(null)
  const [{ error: createBillError, fetching: createBillFetching }, createBill] =
    useMutation(CreateBillDocument)

  const [{ data: listBillOrdersData, fetching: listBillOrdersFetching }] =
    useQuery({
      query: ListBillOrdersDocument,
      variables: { input: { billId: billId ?? '' } },
      requestPolicy: 'network-only',
      pause: !billId,
    })

  const [{ data: getPaymentsData, fetching: getPaymentsFetching }] = useQuery({
    query: GetPaymentsDocument,
    variables: { paymentTargetId: billId ?? '' },
    requestPolicy: 'network-only',
    pause: !billId,
  })

  const lastCompletedPaymentIntentId =
    getPaymentsData?.getPayments.lastCompletedPaymentIntentId

  const {
    paymentMethods,
    paymentMethodsFetching,
    hasStripe,
    ...paymentFormMethodsProps
  } = usePaymentFormMethods({
    totalInCents,
    paymentProcessors,
    venue,
    isNewGroupOrder,
  })

  const {
    stripePaymentIntentFetching,
    stripePaymentIntentError,
    stripePaymentIntentId,
  } = usePaymentIntent({
    totalInCents,
    currency: venue.currency,
    venueId: venue.id,
    billId,
    lastCompletedPaymentIntentId,
    enabled: hasStripe && !!getPaymentsData && totalInCents > 0,
  })

  const lastOrder = (listBillOrdersData?.listBillOrders.orders || []).find(
    (order) =>
      [
        CoreOrderingOrderStatus.Pending,
        CoreOrderingOrderStatus.Placed,
        CoreOrderingOrderStatus.Placing,
      ].includes(order.status),
  )

  const lastPayment = (getPaymentsData?.getPayments.items || [])
    .filter(
      (p) =>
        ['REJECTED', 'SUCCEEDED', 'PENDING'].includes(p.paymentStatus) &&
        p.amount > 0,
    )
    .find(
      ({ updatedAt, orderIds }) =>
        lastOrder &&
        orderIds.includes(lastOrder.id) &&
        differenceInSeconds(new Date(), new Date(updatedAt)) < 30,
    )

  useEffect(() => {
    if (lastPayment) {
      logEventLegacy('Viewed recent payment notification', {
        paymentStatus: lastPayment.paymentStatus,
      })
    }
  }, [lastPayment, logEventLegacy])

  useEffectOnce(() => {
    void createBill({
      input: {
        currency,
        venueId,
        type: BillType.Private,
      },
    }).then(({ data }) => {
      if (data) {
        setBillId(data.createBill.billId)
      }
    })
  })

  const loggerProps = useMemo(
    () => ({
      paymentIntentId: stripePaymentIntentId,
    }),
    [stripePaymentIntentId],
  )

  const defaultPaymentMethod = isNew
    ? paymentMethods.find(
        (paymentMethod) => paymentMethod.key === PaymentMethodKey.StripeNewCard,
      ) || paymentMethods[0]
    : paymentMethods[0]

  if (paymentMethodsFetching || listBillOrdersFetching || getPaymentsFetching) {
    return <>{loader}</>
  }

  return (
    <PaymentControllerContext.Provider
      value={{
        ...rest,
        ...paymentFormMethodsProps,
        isNewGroupOrder,
        defaultPaymentMethod,
        stripeOrBillLoading: stripePaymentIntentFetching || createBillFetching,
        stripeOrBillError: stripePaymentIntentError || createBillError,
        stripePaymentIntentId,
        hasStripe,
        cart,
        isNew,
        paymentProcessors,
        totalInCents,
        currentUser,
        venue,
        paymentMethods,
        showEmailFieldWhenDineIn,
        billId,
        lastCompletedPaymentIntentId,
        lastPayment,
        currency: venue.currency,
      }}
    >
      <LoggerProvider properties={loggerProps}>{children}</LoggerProvider>
    </PaymentControllerContext.Provider>
  )
}

export type PaymentControllerContext = PaymentControllerProps &
  NewPaymentProps &
  Omit<
    UsePaymentMethodsReturnType,
    'paymentRequestFetching' | 'paymentMethodsFetching'
  > & {
    billId?: string | null
    lastCompletedPaymentIntentId?: string | null
    lastPayment: GetPaymentsPaymentResponse | undefined
    currency: string
  } & {
    stripeOrBillLoading: boolean
    stripeOrBillError?: CombinedError
  } & Pick<ReturnType<typeof usePaymentIntent>, 'stripePaymentIntentId'>

export const PaymentControllerContext =
  createContext<PaymentControllerContext | null>(null)

export const usePaymentControllerContext = () => {
  const value = useContext(PaymentControllerContext)

  if (!value) {
    throw new Error('PaymentController Context must be provided')
  }

  return value
}

export const WithPaymentContext = ({
  children,
}: {
  children: (props: PaymentControllerContext) => ReactNode
}) => {
  const props = usePaymentControllerContext()

  return <>{children(props)}</>
}
